import type { Dispatch } from 'react';

import { _isEmptyString, _isNil } from '@/littledash';

import type { StepFormAction } from '@/components/UI/StepForm/StepForm.model';
import type { SummaryDisplayValue, SummaryNormalizedCellChange, SummaryRenderer } from './Summary.models';
import type { CreateBulkDosingState } from '../CreateDosing.utils';

export const formatSummaryDisplayValue = (
  value: SummaryDisplayValue,
  formatNumber: (value: string | number, exponential?: boolean, digits?: number) => string | number
): string => {
  if (value.display_value === null || value.display_value === undefined) {
    return '-';
  }

  return `${formatNumber(value.display_value, true, 3)} ${value.display_unit}`;
};

export const createSummaryDisplayValueRenderer = <TCore, TCellProperties>({
  callTextRenderer,
  formatNumber,
}: {
  callTextRenderer: (
    thisArg: unknown,
    instance: TCore,
    td: HTMLTableCellElement,
    row: number,
    col: number,
    prop: string | number,
    value: SummaryDisplayValue,
    cellProperties: TCellProperties
  ) => void;
  formatNumber: (value: string | number, exponential?: boolean, digits?: number) => string | number;
}): SummaryRenderer<TCore, TCellProperties> => {
  return function summaryDisplayValueRenderer(this: unknown, instance, td, row, col, prop, value, cellProperties) {
    callTextRenderer(this, instance, td, row, col, prop, value, cellProperties);
    td.innerHTML = formatSummaryDisplayValue(value, formatNumber);
  };
};

export const summaryCommentValidator = (
  value: null | undefined | string,
  callback: (isValid: boolean) => void
): void => {
  callback(_isNil(value) || _isEmptyString(value) || value.length < 256);
};

export const createSummarySpreadsheetSettings = <TRenderer>({
  displayValueRenderer,
  dateTimeColumnType,
}: {
  displayValueRenderer: TRenderer;
  dateTimeColumnType: string;
}) => ({
  colHeaders: [
    'Cage name',
    'Animal name',
    'Group name',
    'Treatment name',
    'Dose',
    'Dose volume',
    'Stock',
    'Volume',
    'Time',
    'Comment',
  ],
  columns: [
    { data: 'cage_name', type: 'text', value: 'cage_name', readOnly: true },
    { data: 'animal_name', type: 'text', value: 'animal_name', readOnly: true },
    { data: 'group_name', type: 'text', value: 'group_name', readOnly: true },
    { data: 'treatment_name', type: 'text', value: 'treatment_name', readOnly: true },
    { data: 'dose', type: 'text', value: 'dose', readOnly: true, renderer: displayValueRenderer, width: '100px' },
    { data: 'dose_volume', type: 'text', value: 'dose_volume', readOnly: true, renderer: displayValueRenderer },
    { data: 'stock', type: 'text', value: 'stock', readOnly: true, renderer: displayValueRenderer, width: '100px' },
    { data: 'volume', type: 'text', value: 'volume', readOnly: true, renderer: displayValueRenderer, width: '100px' },
    { data: 'dosed_at', type: dateTimeColumnType, value: 'Time', width: '200px' },
    {
      data: 'comment',
      type: 'text',
      value: 'Comment',
      validator: summaryCommentValidator,
      validationErrorMessage: () => 'Exceeds character limit, please shorten to proceed',
    },
  ],
  columnSorting: false,
  className: 'htMiddle',
  manualColumnResize: true,
  rowHeights: 50,
  height: 500,
  autoColumnSize: true,
  stretchH: 'all' as 'none' | 'all' | 'last' | undefined,
});

export const applyDosageChanges = (
  changes: Array<SummaryNormalizedCellChange> | null,
  state: CreateBulkDosingState,
  dispatch: Dispatch<StepFormAction<CreateBulkDosingState>>
): void => {
  if (_isNil(changes)) {
    return;
  }

  const updatedSelectedDosages = [...state.dosages];
  changes.forEach((change) => {
    const [index, field, , current] = change;
    if (typeof field !== 'string' && typeof field !== 'number') {
      return;
    }
    updatedSelectedDosages[index] = { ...updatedSelectedDosages[index], [field]: current };
  });

  dispatch({
    type: 'updateArray',
    id: 'dosages',
    data: updatedSelectedDosages,
  });
};
